import { Form } from 'antd';
import { useParams } from 'react-router-dom';
import { useCallback, useEffect, useMemo } from 'react';
import { useNotificationMessages } from 'hooks/useNotificationMessages';
import { useAdminUsersQuery, usePurchaseRequestByIdQuery, useUpdatePurchaseRequestManagementMutation } from 'api';

interface UseAssignmentEditProps {
  onCancelModal: () => void;
  open: boolean;
}

export const useAssignmentEdit = (props: UseAssignmentEditProps) => {
  const { onCancelModal, open } = props;
  const [form] = Form.useForm();
  const { id } = useParams() as { id: string };
  const { handleSuccess, handleError } = useNotificationMessages();
  const { data: userData = [] } = useAdminUsersQuery();
  const { data = {} } = usePurchaseRequestByIdQuery({ id });
  const { assignments = {} } = data;

  const [updatePurchaseRequestManagement, { isLoading: isLoadingUpdate }] =
    useUpdatePurchaseRequestManagementMutation();

  const initialAssignment: Record<string, string> = useMemo(
    () => ({
      salesManagerId: assignments?.salesManager?.id,
      salesManagerAssistantId: assignments?.salesManagerAssistant?.id,
      purchaseManagerId: assignments?.purchaseManager?.id,
      purchaseManagerAssistantId: assignments?.purchaseManagerAssistant?.id,
    }),
    [assignments]
  );
  const handleManagerChange = useCallback(
    (value: string, key: string) => {
      if (value === form.getFieldValue([key])) {
        form.setFieldsValue({ [key]: '' });
      }
    },
    [form]
  );

  const onCancel = useCallback(() => {
    form.resetFields();
    onCancelModal();
  }, [form, onCancelModal]);

  const onFinish = useCallback(async () => {
    try {
      const payload = form.getFieldsValue() || {};
      const isFormChanged = Object.keys(payload).some((key) => initialAssignment[key] !== payload[key]);

      if (!isFormChanged) {
        onCancel();
        return;
      }
      payload.purchaseRequestId = id as string;
      const { data, error }: Record<string, any> = await updatePurchaseRequestManagement(payload);
      if (data?.success) {
        handleSuccess('Purchase Request assignments were successfully updated.');
        onCancel();
      } else {
        handleError(error);
      }
    } catch (err) {
      handleError(err);
    }
  }, [updatePurchaseRequestManagement, handleSuccess, handleError, id, onCancel, form, initialAssignment]);

  useEffect(() => {
    if (open && initialAssignment) {
      form.setFieldsValue(initialAssignment);
    }
  }, [form, open, initialAssignment]);

  return {
    form,
    isLoadingUpdate,
    onFinish,
    onCancel,
    handleManagerChange,
    userData,
  };
};
